import {createAsyncThunk} from '@reduxjs/toolkit';
import type {RootState} from '@/store';
import {GA4EventName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {isUserLoggedIn} from '@Libraries/user.library';
import {getAjaxErrorCode} from '@Utils/ajax.util';
import {addStockFavorite, getStockFavoriteKeys, getStockFavoritesToToggleTogether, removeStockFavorite} from '@Panels/stock-panel/stock-favorites.library';
import {
  clearStockFavoriteTogglePending,
  isStockFavoriteTogglePending,
  isStockItemFavorited,
  markStockFavorite,
  markStockFavoriteTogglePending,
  unmarkStockFavorite,
} from '@Panels/stock-panel/stock-favorites-reducer';
import type {StockFavoriteIdentity, StockFavoriteKey} from '@Panels/stock-panel/stock-favorites.types';
import {FAVORITES_LIMIT_REACHED_ERROR_CODE, StockFavoritesLoadStatus} from '@Panels/stock-panel/stock-favorites.types';

const RELOADABLE_LOAD_STATUSES: ReadonlySet<StockFavoritesLoadStatus> = new Set([StockFavoritesLoadStatus.IDLE, StockFavoritesLoadStatus.ERROR]);

/**
 * Loads the user's favorite ids once for the life of the store: every stock panel dispatches this on mount and
 * the condition collapses the repeats, so two panels mounting together cost one request.
 */
export const loadStockFavoriteKeys = createAsyncThunk<StockFavoriteKey[], undefined, {state: RootState}>(
  'stockFavorites/loadKeys',
  getStockFavoriteKeys,
  {
    condition: (_, {getState}): boolean => isUserLoggedIn() && RELOADABLE_LOAD_STATUSES.has(getState().stockFavorites.loadStatus),
  }
);

/**
 * Flips the heart first and tells the server after, so every tile showing the item updates at once, and resolves
 * with whether the item is now a favorite. A refused toggle flips it back and rejects with the server's error
 * code; a toggle already in flight is left alone.
 */
export const toggleStockFavorite = createAsyncThunk<boolean, StockFavoriteIdentity, {state: RootState; rejectValue: string | undefined}>(
  'stockFavorites/toggle',
  async (identity, {dispatch, getState, rejectWithValue}) => {
    const key: StockFavoriteKey = {itemSource: identity.itemSource, itemId: identity.itemId};
    const wasFavorited = isStockItemFavorited(getState(), key);
    if (isStockFavoriteTogglePending(getState(), key)) {
      return wasFavorited;
    }

    dispatch(markStockFavoriteTogglePending(key));
    dispatch(wasFavorited ? unmarkStockFavorite(key) : markStockFavorite(key));
    try {
      if (wasFavorited) {
        await removeStockFavorite(key);
        trackPosterBuilderGA4Events(GA4EventName.MEDIA_MODAL_FAVORITE_REMOVE);
      } else {
        await addStockFavorite(identity);
        trackPosterBuilderGA4Events(GA4EventName.MEDIA_MODAL_FAVORITE_ADD);
      }
    } catch (error) {
      dispatch(wasFavorited ? markStockFavorite(key) : unmarkStockFavorite(key));
      return rejectWithValue(getAjaxErrorCode(error));
    } finally {
      dispatch(clearStockFavoriteTogglePending(key));
    }

    return !wasFavorited;
  }
);

/**
 * Toggles a multi-selection as one: removes every item when all are favorites, otherwise adds the ones that are
 * not. Each item flips on its own, so a refusal reverts only that item. Rejects with the cap's error code when any
 * add hit the cap, otherwise with the first refusal's code.
 */
export const toggleStockFavoritesTogether = createAsyncThunk<undefined, StockFavoriteIdentity[], {state: RootState; rejectValue: string | undefined}>(
  'stockFavorites/toggleTogether',
  async (identities, {dispatch, getState, rejectWithValue}) => {
    const identitiesToToggle = getStockFavoritesToToggleTogether(identities, (key) => isStockItemFavorited(getState(), key));
    const actions = await Promise.all(identitiesToToggle.map((identity) => dispatch(toggleStockFavorite(identity))));
    const errorCodes = actions.filter(toggleStockFavorite.rejected.match).map((action) => action.payload);
    if (errorCodes.length === 0) {
      return undefined;
    }
    return rejectWithValue(errorCodes.includes(FAVORITES_LIMIT_REACHED_ERROR_CODE) ? FAVORITES_LIMIT_REACHED_ERROR_CODE : errorCodes[0]);
  },
);
